/* ==========================================================================
   NaturoBest Page System v3 — nb3-difference.js
   --------------------------------------------------------------------------
   Independent of nb-landing.js and nb2-landing.js: its own selectors
   (data-nb3-*), no shared globals. Dependency-free, presentation only.

     1. Carousel — arrows and dots for a native scroll-snap track, shown only
        while the track overflows
     2. Marquee — falls back to a static row when the list cannot fill the
        strip, or when the visitor asks for reduced motion
     3. Reveal — fades sections in as they scroll into view

   Every section links this file; the guard below keeps a second copy from
   binding twice. Re-runs on theme editor section load.
   ========================================================================== */

(function () {
  'use strict';

  if (window.nb3Difference) return;
  window.nb3Difference = true;

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function each(root, selector, fn) {
    Array.prototype.forEach.call(root.querySelectorAll(selector), fn);
  }

  // Run fn at most once per frame, however often the event fires.
  function throttleFrame(fn) {
    var frame = null;
    return function () {
      if (frame) return;
      frame = requestAnimationFrame(function () {
        frame = null;
        fn();
      });
    };
  }

  function onResize(node, fn) {
    // The width can change without the window resizing — the theme editor
    // opening its sidebar, for one.
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(fn).observe(node);
    } else {
      window.addEventListener('resize', fn);
    }
  }

  /* 1. Carousel ------------------------------------------------------------ */

  /* Pages, not cards: nine reviews three-up is three dots. The -1 absorbs the
     sub-pixel remainder of fractional card widths, which would otherwise round
     a full row up to an empty second page. */
  function pageCount(track) {
    var width = track.clientWidth;
    if (width <= 0) return 1;
    return Math.max(1, Math.ceil((track.scrollWidth - 1) / width));
  }

  function currentPage(track, pages) {
    var width = track.clientWidth;
    var max = track.scrollWidth - width;
    if (width <= 0 || max <= 0) return 0;
    // The last page is usually short, so rounding alone would leave the final
    // dot unlit at the very end of the track.
    if (track.scrollLeft >= max - 1) return pages - 1;
    return Math.min(pages - 1, Math.round(track.scrollLeft / width));
  }

  function initCarousel(carousel) {
    if (carousel.dataset.nb3Ready) return;
    carousel.dataset.nb3Ready = 'true';

    var track = carousel.querySelector('[data-nb3-carousel-track]');
    var controls = carousel.querySelector('[data-nb3-carousel-controls]');
    if (!track || !controls) return;

    var prev = controls.querySelector('[data-nb3-carousel-prev]');
    var next = controls.querySelector('[data-nb3-carousel-next]');
    var dotsWrap = controls.querySelector('[data-nb3-carousel-dots]');
    var dotLabel = (dotsWrap && dotsWrap.getAttribute('data-nb3-dot-label')) || 'Page';
    var dots = [];

    function goTo(index) {
      // The browser clamps past either end, so no bounds check.
      track.scrollTo({
        left: index * track.clientWidth,
        behavior: reducedMotion() ? 'auto' : 'smooth'
      });
    }

    function buildDots(count) {
      if (!dotsWrap || dots.length === count) return;
      dotsWrap.innerHTML = '';
      dots = [];
      for (var i = 0; i < count; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'nb3-carousel__dot';
        dot.setAttribute('aria-label', dotLabel + ' ' + (i + 1));
        dot.addEventListener('click', goTo.bind(null, i));
        dotsWrap.appendChild(dot);
        dots.push(dot);
      }
    }

    function sync() {
      var scrollable = track.scrollWidth - track.clientWidth > 1;
      controls.hidden = !scrollable;

      // The markup ships a tabindex so the track is keyboard-scrollable without
      // this file; a row with nowhere to scroll does not deserve a tab stop.
      if (scrollable) {
        track.setAttribute('tabindex', '0');
      } else {
        track.removeAttribute('tabindex');
        return;
      }

      var pages = pageCount(track);
      buildDots(pages);

      var page = currentPage(track, pages);
      dots.forEach(function (dot, i) {
        dot.setAttribute('aria-current', i === page ? 'true' : 'false');
      });

      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
    }

    var queueSync = throttleFrame(sync);

    function step(direction) {
      goTo(currentPage(track, pageCount(track)) + direction);
    }

    if (prev) prev.addEventListener('click', step.bind(null, -1));
    if (next) next.addEventListener('click', step.bind(null, 1));

    track.addEventListener('scroll', queueSync, { passive: true });
    onResize(track, queueSync);
    sync();
  }

  /* 2. Marquee ------------------------------------------------------------- */

  /* The Liquid prints the logo list twice and CSS slides the track by half. If
     one copy is narrower than the strip, a gap opens before the loop wraps —
     so the copy is repeated here until it covers the strip, and the whole
     thing drops to a centred static row for reduced motion. */
  function initMarquee(marquee) {
    if (marquee.dataset.nb3Ready) return;
    marquee.dataset.nb3Ready = 'true';

    var track = marquee.querySelector('[data-nb3-marquee-track]');
    if (!track) return;

    if (reducedMotion()) {
      marquee.classList.add('nb3-marquee--static');
      return;
    }

    var original = Array.prototype.filter.call(track.children, function (el) {
      return el.getAttribute('aria-hidden') !== 'true';
    });
    if (!original.length) return;

    var baseSeconds = parseFloat(marquee.getAttribute('data-nb3-seconds-per-logo')) || 3;

    function fill() {
      // Rebuild from the originals each time, so resizing never compounds.
      each(track, '[data-nb3-clone]', function (el) { el.remove(); });

      var setWidth = original.reduce(function (sum, el) { return sum + el.offsetWidth; }, 0);
      if (setWidth <= 0) return;

      var copies = Math.max(1, Math.ceil(marquee.clientWidth / setWidth));
      var perHalf = [];

      // Half one: the originals plus any extra copies needed to cover the strip.
      for (var i = 1; i < copies; i++) perHalf = perHalf.concat(original);
      perHalf.forEach(appendClone);

      // Half two: a full mirror of half one. The Liquid's own copy is removed
      // first so the two halves match exactly.
      each(track, '[aria-hidden="true"]:not([data-nb3-clone])', function (el) { el.remove(); });
      var halfOne = original.concat(perHalf);
      halfOne.forEach(appendClone);

      marquee.style.setProperty('--nb3-marquee-duration', (halfOne.length * baseSeconds) + 's');
    }

    // Every copy is hidden from assistive tech — the originals already say it.
    function appendClone(el) {
      var clone = el.cloneNode(true);
      clone.setAttribute('data-nb3-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-shopify-editor-block');
      track.appendChild(clone);
    }

    // Logos are eager-loaded, but their widths are only final once decoded.
    var images = track.querySelectorAll('img');
    var pending = 0;
    Array.prototype.forEach.call(images, function (img) {
      if (img.complete) return;
      pending++;
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });

    function done() {
      pending--;
      if (pending === 0) fill();
    }

    fill();

    var lastWidth = marquee.clientWidth;
    onResize(marquee, throttleFrame(function () {
      if (marquee.clientWidth === lastWidth) return;
      lastWidth = marquee.clientWidth;
      fill();
    }));
  }

  /* 3. Reveal on scroll ---------------------------------------------------- */

  /* Opt-in per page (NB3 Page Style sets data-nb3-reveal on <html>). Skipped in
     the theme editor, where hidden content would make sections look broken
     while being edited. Anything already on screen is left alone — only
     elements below the fold are armed, so nothing visible ever blinks out. */
  var revealObserver = null;

  function initReveal(root) {
    if (!document.documentElement.hasAttribute('data-nb3-reveal')) return;
    if (reducedMotion() || (window.Shopify && window.Shopify.designMode)) return;
    if (typeof IntersectionObserver !== 'function') return;

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('nb3-reveal--in');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }

    var fold = window.innerHeight;

    each(root, '[data-nb3-reveal-group]', function (group) {
      if (group.dataset.nb3RevealReady) return;
      group.dataset.nb3RevealReady = 'true';

      Array.prototype.forEach.call(group.children, function (el, index) {
        if (el.getBoundingClientRect().top < fold) return;
        // Stagger siblings in a row, capped so a long grid does not crawl.
        el.style.setProperty('--nb3-delay', Math.min(index % 5, 4) * 80 + 'ms');
        el.classList.add('nb3-reveal--armed');
        revealObserver.observe(el);
      });
    });
  }

  /* Boot ------------------------------------------------------------------- */

  function init(root) {
    var scope = root || document;
    each(scope, '[data-nb3-carousel]', initCarousel);
    each(scope, '[data-nb3-marquee]', initMarquee);
    initReveal(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
